import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Download, ClipboardList, Calendar, Inbox, FileText, Trash2, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useTenant } from '../components/TenantContext.jsx';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty } from '../components/ui/Card.jsx';
import { listLeadScenarios, listLeads, deleteLead } from '../api/leads.js';
import { LeadsCalendar } from '../components/Leads/LeadsCalendar.jsx';

/**
 * CustomerLeadsPage — 收單及預約
 *
 * Flow:
 *   1. user 選 avatar
 *   2. 左欄列該 avatar 的 formCollection scenarios（一個 avatar 可多個收單情境）
 *   3. 右欄 tabs: [名單 table] / [月曆]（月曆只有含 date field 才顯示）
 *   4. 下載 Excel
 */
const LEADS_LIMIT = 5000;   // 同後端 /list 上限

export function CustomerLeadsPage() {
    const { t } = useTranslation('leads');
    const { hostId, isAdmin } = useTenant();   // 刪收單（單筆 / 批次）限 tenant admin（後端 tenantGuard）

    const [allScenarios, setAllScenarios] = useState([]);
    const [scenariosLoading, setScenariosLoading] = useState(true);
    const [scenariosErr, setScenariosErr] = useState(null);

    const [selectedAvatarId, setSelectedAvatarId] = useState(null);
    const [selectedScenarioId, setSelectedScenarioId] = useState(null);

    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [leadsErr, setLeadsErr] = useState(null);
    const [dateFrom, setDateFrom] = useState('');   // YYYY-MM-DD，依收單時間；空 = 不限
    const [dateTo, setDateTo] = useState('');
    const hasDateRange = !!(dateFrom || dateTo);

    const [tab, setTab] = useState('list');  // 'list' | 'calendar'
    const [selectedLeadIds, setSelectedLeadIds] = useState(() => new Set());
    const [batchDeleting, setBatchDeleting] = useState(false);

    // === Load scenarios on mount ===
    useEffect(() => {
        if (!hostId) return;
        setScenariosLoading(true);
        listLeadScenarios(hostId)
            .then((r) => {
                const scs = r?.scenarios || [];
                setAllScenarios(scs);
                // auto-select first avatar with scenarios
                if (scs.length && !selectedAvatarId) {
                    setSelectedAvatarId(scs[0].avatarId);
                    setSelectedScenarioId(scs[0].scenarioId);
                }
            })
            .catch((e) => setScenariosErr(e))
            .finally(() => setScenariosLoading(false));
        // eslint-disable-next-line
    }, [hostId]);

    // Avatars 列表（去重）
    const avatars = useMemo(() => {
        const seen = new Map();
        allScenarios.forEach((s) => {
            if (!seen.has(s.avatarId)) {
                seen.set(s.avatarId, {
                    avatarId: s.avatarId,
                    avatarName: s.avatarName,
                    scenarioCount: 0,
                });
            }
            seen.get(s.avatarId).scenarioCount++;
        });
        return Array.from(seen.values());
    }, [allScenarios]);

    // 該 avatar 的 scenarios
    const avatarScenarios = useMemo(
        () => allScenarios.filter((s) => s.avatarId === selectedAvatarId),
        [allScenarios, selectedAvatarId]
    );

    // 當前選的 scenario detail
    const currentScenario = useMemo(
        () => avatarScenarios.find((s) => s.scenarioId === selectedScenarioId),
        [avatarScenarios, selectedScenarioId]
    );

    // 顯示欄位 = 當前 scenario 欄位 ∪ 每筆 lead 自帶 schema snapshot 的欄位。
    // → admin 改過表單欄位後,舊 lead 用它當時的 schema(schemaFieldsSnapshot),
    //   新 lead 用新 schema,table 欄位取聯集,新舊資料都顯示得出來(不會變「—」消失)。
    //   沒 snapshot 的更舊資料 fallback 用 collectionData 的 key。
    const displayFields = useMemo(() => {
        const out = [];
        const seen = new Set();
        const push = (f) => {
            if (!f?.name || seen.has(f.name)) return;
            seen.add(f.name);
            out.push({ name: f.name, label: f.label || f.name, required: !!f.required });
        };
        (currentScenario?.fields || []).forEach(push);
        for (const l of leads) {
            const snap = (Array.isArray(l.schemaFieldsSnapshot) && l.schemaFieldsSnapshot.length)
                ? l.schemaFieldsSnapshot
                : Object.keys(l.collectionData || {}).map((k) => ({ name: k, label: k }));
            snap.forEach(push);
        }
        return out;
    }, [currentScenario, leads]);

    // 偵測該 scenario 是否含 date 欄位 → 顯示月曆 tab
    const dateField = useMemo(() => {
        const fields = currentScenario?.fields || [];
        return fields.find(
            (f) =>
                f.type === 'date' ||
                f.type === 'datetime' ||
                /(date|appointment|預約|日期)/i.test(f.name) ||
                /(預約|日期)/.test(f.label || '')
        );
    }, [currentScenario]);
    const timeField = useMemo(() => {
        const fields = currentScenario?.fields || [];
        return fields.find(
            (f) =>
                f.type === 'time' ||
                /(time|時段|時間)/i.test(f.name) ||
                /(時段|時間)/.test(f.label || '')
        );
    }, [currentScenario]);
    const hasCalendarMode = !!dateField;

    // === Load leads on scenario change ===
    useEffect(() => {
        if (!hostId || !selectedScenarioId || !selectedAvatarId) {
            setLeads([]);
            return;
        }
        setLeadsLoading(true);
        setLeadsErr(null);
        setSelectedLeadIds(new Set());   // 換 scenario 清空選取
        let stale = false;   // 日期欄位手打時會連發查詢，較早的回應不可蓋掉較新的
        listLeads({ hostId, scenarioId: selectedScenarioId, avatarId: selectedAvatarId, limit: LEADS_LIMIT, from: dateFrom, to: dateTo })
            .then((r) => { if (!stale) setLeads(r?.leads || []); })
            .catch((e) => { if (!stale) setLeadsErr(e); })
            .finally(() => { if (!stale) setLeadsLoading(false); });
        return () => { stale = true; };
    }, [hostId, selectedScenarioId, selectedAvatarId, dateFrom, dateTo]);

    // 切換 avatar：自動選第一個 scenario
    const handleAvatarChange = (avatarId) => {
        setSelectedAvatarId(avatarId);
        const firstScenario = allScenarios.find((s) => s.avatarId === avatarId);
        setSelectedScenarioId(firstScenario?.scenarioId || null);
        setTab('list');
    };

    // 切 scenario 時若舊 tab='calendar' 但新的沒 date 欄 → reset to list
    useEffect(() => {
        if (tab === 'calendar' && !hasCalendarMode) setTab('list');
    }, [tab, hasCalendarMode]);

    // === 刪除單筆 lead ===
    const handleDeleteLead = async (leadId) => {
        if (!hostId || !leadId || !selectedScenarioId || !selectedAvatarId) return;
        if (!confirm(t('confirm.deleteOne'))) return;
        try {
            await deleteLead(leadId, { hostId, scenarioId: selectedScenarioId, avatarId: selectedAvatarId });
            setLeads((prev) => prev.filter((l) => l.id !== leadId));
            setSelectedLeadIds((prev) => {
                if (!prev.has(leadId)) return prev;
                const next = new Set(prev);
                next.delete(leadId);
                return next;
            });
        } catch (e) {
            alert(t('alert.deleteFailed', { msg: e.message }));
        }
    };

    // === 切換單筆 / 全選 ===
    const handleToggleLead = (leadId) => {
        setSelectedLeadIds((prev) => {
            const next = new Set(prev);
            if (next.has(leadId)) next.delete(leadId);
            else next.add(leadId);
            return next;
        });
    };
    const handleToggleAll = () => {
        setSelectedLeadIds((prev) =>
            prev.size === leads.length && leads.length > 0
                ? new Set()
                : new Set(leads.map((l) => l.id))
        );
    };

    // === 批次刪除 ===
    const handleBatchDelete = async () => {
        if (!hostId || !selectedScenarioId || !selectedAvatarId || selectedLeadIds.size === 0) return;
        const ids = Array.from(selectedLeadIds);
        if (!confirm(t('confirm.deleteSelected', { count: ids.length }))) return;
        setBatchDeleting(true);
        const failed = [];
        // 並行送 delete — 失敗的記下來顯示
        await Promise.all(ids.map((id) =>
            deleteLead(id, { hostId, scenarioId: selectedScenarioId, avatarId: selectedAvatarId })
                .catch((e) => { failed.push({ id, error: e.message }); })
        ));
        const okIds = new Set(ids.filter((id) => !failed.find((f) => f.id === id)));
        setLeads((prev) => prev.filter((l) => !okIds.has(l.id)));
        setSelectedLeadIds((prev) => {
            const next = new Set(prev);
            for (const id of okIds) next.delete(id);
            return next;
        });
        setBatchDeleting(false);
        if (failed.length > 0) {
            alert(`${t('alert.batchResult', { ok: okIds.size, failed: failed.length })}\n${failed.map((f) => f.error).join('\n')}`);
        }
    };

    // === Excel 下載 ===
    const handleDownloadExcel = () => {
        if (!currentScenario || leads.length === 0) return;
        const fields = displayFields;   // 含新舊欄位聯集,匯出不漏舊資料

        // build rows: 收單時間 + 動態欄位
        const rows = leads.map((lead) => {
            const row = {
                [t('table.receivedAt')]: lead.createdAt ? formatDateTime(lead.createdAt) : '',
            };
            fields.forEach((f) => {
                row[f.label || f.name] = lead.collectionData?.[f.name] ?? '';
            });
            if (lead.apiOk != null) {
                row[t('table.apiResult')] = lead.apiOk ? t('table.apiOk') : `${t('table.apiFailed')} ${lead.apiError || ''}`.trim();
                row[t('table.apiResponse')] = apiResponseText(lead.apiResponse);
            }
            return row;
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, currentScenario.scenarioName.substring(0, 31));

        const datePart = hasDateRange
            ? `${dateFrom}~${dateTo}`.replace(/-/g, '')
            : new Date().toISOString().substring(0, 10).replace(/-/g, '');
        const filename = `${currentScenario.avatarName}_${currentScenario.scenarioName}_${datePart}.xlsx`
            .replace(/[\\/:*?"<>|]/g, '_');
        XLSX.writeFile(wb, filename);
    };

    if (scenariosLoading) {
        return (
            <div>
                <PageHeader eyebrow="CUSTOMER LEADS & APPOINTMENTS" title={t('title')} />
                <div className="text-sm text-ink-400 italic mt-4">{t('loadingScenarios')}</div>
            </div>
        );
    }
    if (scenariosErr) {
        return (
            <div>
                <PageHeader eyebrow="CUSTOMER LEADS & APPOINTMENTS" title={t('title')} />
                <div className="text-sm text-red-600 mt-4">{t('loadFailed', { msg: scenariosErr.message })}</div>
            </div>
        );
    }

    return (
        <div>
            <PageHeader
                eyebrow="CUSTOMER LEADS & APPOINTMENTS"
                title={t('title')}
                description={t('description')}
                actions={
                    <>
                        <div className="flex items-center gap-1.5 text-sm text-ink-500">
                            <input
                                type="date"
                                value={dateFrom}
                                max={dateTo || undefined}
                                onChange={(e) => setDateFrom(e.target.value)}
                                aria-label={t('dateRange.from')}
                                className="text-sm px-2 py-2 border border-cream-300 rounded-md bg-white text-ink-900 hover:border-accent-400 focus:border-accent-500 focus:outline-none"
                            />
                            <span>~</span>
                            <input
                                type="date"
                                value={dateTo}
                                min={dateFrom || undefined}
                                onChange={(e) => setDateTo(e.target.value)}
                                aria-label={t('dateRange.to')}
                                className="text-sm px-2 py-2 border border-cream-300 rounded-md bg-white text-ink-900 hover:border-accent-400 focus:border-accent-500 focus:outline-none"
                            />
                            {hasDateRange && (
                                <button
                                    type="button"
                                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                                    className="px-2 py-1 text-xs rounded border border-cream-300 bg-white text-ink-700 hover:bg-cream-50"
                                >
                                    {t('dateRange.all')}
                                </button>
                            )}
                        </div>
                        {avatars.length > 0 && (
                            <select
                                value={selectedAvatarId || ''}
                                onChange={(e) => handleAvatarChange(e.target.value)}
                                className="text-sm px-3 py-2 border border-cream-300 rounded-md bg-white text-ink-900 hover:border-accent-400 focus:border-accent-500 focus:outline-none min-w-[240px] cursor-pointer"
                            >
                                {avatars.map((a) => (
                                    <option key={a.avatarId} value={a.avatarId}>
                                        {t('avatarOption', { name: a.avatarName, count: a.scenarioCount })}
                                    </option>
                                ))}
                            </select>
                        )}
                        <button
                            onClick={handleDownloadExcel}
                            disabled={!currentScenario || leads.length === 0}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-ink-900 text-cream-50 hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                        >
                            <Download size={14} />
                            <span>{t('downloadExcel')}</span>
                        </button>
                    </>
                }
            />

            {avatars.length === 0 ? (
                <Card>
                    <Empty
                        icon={Inbox}
                        title={t('emptyScenarios.title')}
                        description={t('emptyScenarios.description')}
                    />
                </Card>
            ) : (
                <div className="grid grid-cols-[260px_1fr] gap-4 min-h-[500px]">
                    {/* === LEFT: scenarios === */}
                    <div className="space-y-1.5">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 px-1 mb-2">
                            {t('scenarioListHeader', { count: avatarScenarios.length })}
                        </div>
                        {avatarScenarios.map((s) => {
                            const sDateField = (s.fields || []).find(
                                (f) =>
                                    f.type === 'date' ||
                                    f.type === 'datetime' ||
                                    /(date|appointment|預約|日期)/i.test(f.name)
                            );
                            const Icon = sDateField ? Calendar : ClipboardList;
                            const isSelected = s.scenarioId === selectedScenarioId;
                            return (
                                <button
                                    key={s.scenarioId}
                                    onClick={() => setSelectedScenarioId(s.scenarioId)}
                                    className={[
                                        'w-full text-left p-3 rounded-md border transition group',
                                        isSelected
                                            ? 'bg-accent-50 border-accent-300'
                                            : 'bg-white border-cream-200 hover:border-accent-300 hover:bg-cream-50',
                                    ].join(' ')}
                                >
                                    <div className="flex items-start gap-2.5">
                                        <div
                                            className={[
                                                'inline-flex items-center justify-center size-8 rounded-md shrink-0',
                                                isSelected ? 'bg-accent-100' : 'bg-cream-100 group-hover:bg-accent-50',
                                            ].join(' ')}
                                        >
                                            <Icon size={15} strokeWidth={1.6} className={isSelected ? 'text-accent-800' : 'text-accent-700'} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-ink-900 truncate">
                                                {s.scenarioName}
                                            </div>
                                            <div className="text-[11px] text-ink-400 mt-0.5 truncate">
                                                {(s.fields || []).length > 0 && t('fieldCount', { count: s.fields.length })}
                                                {sDateField && <span className="ml-1 text-accent-700">{t('hasDate')}</span>}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* === RIGHT: leads content === */}
                    <Card padded={false} className="flex flex-col min-h-0">
                        {currentScenario && (
                            <div className="px-5 py-4 border-b border-cream-200 flex items-center justify-between">
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-0.5">
                                        scenario · {currentScenario.scenarioId}
                                    </div>
                                    <h2 className="heading-display text-lg font-semibold text-ink-900">
                                        {currentScenario.scenarioName}
                                    </h2>
                                </div>
                                {/* Tabs (only show calendar tab if has date field) */}
                                {hasCalendarMode && (
                                    <div className="inline-flex rounded-md bg-cream-100 p-0.5 border border-cream-200">
                                        {[
                                            { k: 'list', icon: FileText, labelKey: 'tab.list' },
                                            { k: 'calendar', icon: Calendar, labelKey: 'tab.calendar' },
                                        ].map((tabItem) => {
                                            const Icon = tabItem.icon;
                                            return (
                                                <button
                                                    key={tabItem.k}
                                                    onClick={() => setTab(tabItem.k)}
                                                    className={[
                                                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition font-medium',
                                                        tab === tabItem.k
                                                            ? 'bg-white text-ink-900 shadow-sm'
                                                            : 'text-ink-500 hover:text-ink-700',
                                                    ].join(' ')}
                                                >
                                                    <Icon size={12} strokeWidth={1.8} />
                                                    {t(tabItem.labelKey)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="p-4 flex-1 min-h-0">
                            {!leadsLoading && !leadsErr && leads.length >= LEADS_LIMIT && (
                                <div className="mb-3 text-xs text-amber-700">{t('dateRange.limitHint', { count: LEADS_LIMIT })}</div>
                            )}
                            {leadsLoading ? (
                                <div className="text-sm text-ink-400 italic">{t('loadingLeads')}</div>
                            ) : leadsErr ? (
                                <div className="text-sm text-red-600">
                                    {t('loadFailed', { msg: leadsErr.message })}
                                    {leadsErr.indexUrl && (
                                        <div className="mt-2 text-xs">
                                            {t('missingIndex')}
                                            <a href={leadsErr.indexUrl} target="_blank" rel="noreferrer" className="underline ml-1">link</a>
                                        </div>
                                    )}
                                </div>
                            ) : leads.length === 0 ? (
                                <Empty
                                    icon={Inbox}
                                    title={t('emptyLeads.title')}
                                    description={hasDateRange
                                        ? t('dateRange.empty')
                                        : t('emptyLeads.description', { name: currentScenario?.scenarioName || t('thisScenario') })}
                                />
                            ) : tab === 'calendar' ? (
                                <LeadsCalendar
                                    leads={leads}
                                    dateFieldName={dateField?.name}
                                    timeFieldName={timeField?.name}
                                />
                            ) : (
                                <>
                                    {selectedLeadIds.size > 0 && (
                                        <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 bg-accent-50 border border-accent-200 rounded-md">
                                            <div className="text-sm text-ink-900">
                                                <Trans
                                                    i18nKey="selectedCount"
                                                    t={t}
                                                    values={{ count: selectedLeadIds.size }}
                                                    components={{ b: <span className="font-semibold tabular-nums" /> }}
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setSelectedLeadIds(new Set())}
                                                    disabled={batchDeleting}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-cream-300 bg-white text-ink-700 hover:bg-cream-50 disabled:opacity-50"
                                                >
                                                    <X size={12} /> {t('clearSelection')}
                                                </button>
                                                <button
                                                    onClick={handleBatchDelete}
                                                    disabled={batchDeleting}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded bg-danger-500 text-white hover:bg-danger-600 disabled:opacity-50"
                                                >
                                                    <Trash2 size={12} />
                                                    {batchDeleting ? t('deleting') : t('batchDelete', { count: selectedLeadIds.size })}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <LeadsTable
                                        leads={leads}
                                        fields={displayFields}
                                        onDelete={isAdmin ? handleDeleteLead : undefined}
                                        selectedIds={selectedLeadIds}
                                        onToggleLead={isAdmin ? handleToggleLead : undefined}
                                        onToggleAll={isAdmin ? handleToggleAll : undefined}
                                    />
                                </>
                            )}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

function LeadsTable({ leads, fields, onDelete, selectedIds, onToggleLead, onToggleAll }) {
    const apiCols = leads.some((l) => l.apiOk != null);   // saveAsLead 的 API 收單才有送出結果
    const { t } = useTranslation('leads');
    const selectable = !!onToggleLead;
    const allChecked = selectable && leads.length > 0 && selectedIds.size === leads.length;
    const someChecked = selectable && selectedIds.size > 0 && selectedIds.size < leads.length;
    const headerCheckRef = useRef(null);
    useEffect(() => {
        if (headerCheckRef.current) headerCheckRef.current.indeterminate = someChecked;
    }, [someChecked]);

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-cream-200 text-left">
                        {selectable && (
                            <th className="px-3 py-2 w-10">
                                <input
                                    ref={headerCheckRef}
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={onToggleAll}
                                    aria-label={t('aria.selectAll')}
                                    className="size-4 rounded border-cream-300 cursor-pointer accent-accent-700"
                                />
                            </th>
                        )}
                        <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-400 font-medium">
                            {t('table.receivedAt')}
                        </th>
                        {fields.map((f) => (
                            <th
                                key={f.name}
                                className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-400 font-medium"
                            >
                                {f.label || f.name}
                                {f.required && <span className="text-accent-700 ml-0.5">*</span>}
                            </th>
                        ))}
                        {apiCols && [t('table.apiResult'), t('table.apiResponse')].map((h) => (
                            <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-400 font-medium">{h}</th>
                        ))}
                        {onDelete && <th className="px-3 py-2 w-10" aria-label={t('common:actions.delete')} />}
                    </tr>
                </thead>
                <tbody>
                    {leads.map((lead) => {
                        const checked = selectable && selectedIds.has(lead.id);
                        return (
                            <tr
                                key={lead.id}
                                className={[
                                    'border-b border-cream-100 transition group',
                                    checked ? 'bg-accent-50/60' : 'hover:bg-cream-50',
                                ].join(' ')}
                            >
                                {selectable && (
                                    <td className="px-3 py-2.5">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => onToggleLead(lead.id)}
                                            aria-label={t('aria.selectOne')}
                                            className="size-4 rounded border-cream-300 cursor-pointer accent-accent-700"
                                        />
                                    </td>
                                )}
                                <td className="px-3 py-2.5 text-ink-700 tnum whitespace-nowrap">
                                    {lead.createdAt ? formatDateTime(lead.createdAt) : '—'}
                                </td>
                                {fields.map((f) => {
                                    // 長內容（巢狀 JSON、網址）不撐寬表格：限寬＋強制換行，最多 3 行，hover 看全文
                                    const v = formatFieldValue(lead.collectionData?.[f.name], f.type);
                                    return (
                                        <td key={f.name} className="px-3 py-2.5 text-ink-900 max-w-xs">
                                            <div className="line-clamp-3 break-words [overflow-wrap:anywhere]" title={v}>{v}</div>
                                        </td>
                                    );
                                })}
                                {apiCols && (
                                    <>
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            {lead.apiOk
                                                ? <span className="rounded bg-success-100 px-1.5 py-0.5 text-xs text-success-500">{t('table.apiOk')}</span>
                                                : <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700" title={lead.apiError || ''}>{t('table.apiFailed')}</span>}
                                        </td>
                                        <td className="px-3 py-2.5 text-ink-700 max-w-xs">
                                            {lead.apiResponse ? (
                                                <details>
                                                    <summary className="cursor-pointer truncate">{apiResponseText(lead.apiResponse).slice(0, 60)}</summary>
                                                    <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all text-xs">{apiResponseText(lead.apiResponse, 2)}</pre>
                                                </details>
                                            ) : '—'}
                                        </td>
                                    </>
                                )}
                                {onDelete && (
                                    <td className="px-3 py-2.5 text-right">
                                        <button
                                            onClick={() => onDelete(lead.id)}
                                            title={t('deleteRowTitle')}
                                            className="inline-flex items-center justify-center size-7 rounded text-ink-300 hover:text-danger-500 hover:bg-danger-50 transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// saveAsLead 存的 API 回傳（JSON 字串，最多 4KB）→ 顯示用文字；indent 給展開後的完整內容
function apiResponseText(raw, indent) {
    try {
        const v = JSON.parse(raw);
        return JSON.stringify(v?.data ?? v, null, indent);
    } catch { return String(raw || ''); }
}

function formatDateTime(iso) {
    try {
        const d = new Date(iso);
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const da = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${mo}-${da} ${hh}:${mm}`;
    } catch {
        return String(iso);
    }
}

function formatFieldValue(val, type) {
    if (val === undefined || val === null || val === '') return '—';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
}
